import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { DollarSign, Info, Megaphone, Scale } from "lucide-react";

import { SettingsSection } from "src/components/SettingsDialog/SettingsSection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "src/components/ui/accordion";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { ScrollArea, ScrollBar } from "src/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { useAuth } from "src/lib/auth";
import { getMonthsInAYear } from "src/lib/date";
import { useApiMutation, useApiQuery } from "src/lib/hooks/useApi";
import { useNumberFormat } from "src/lib/hooks/useNumberFormat";
import {
  ANALYTICS_INPUTS_QUERY,
  UPSERT_ANALYTICS_INPUTS_MUTATION,
} from "src/lib/queries/analytics";
import { cn } from "src/lib/utils";

const sections = [
  {
    title: "Fixed Costs",
    icon: DollarSign,
    description:
      "Enter costs that do not change with sales volume, such as rent, salaries, or utilities",
    inputs: [
      {
        name: "operatingExpense",
        label: "Operating Expenses",
        tooltip:
          "Costs that are not directly tied to production, such as rent, utilities, and salaries",
        prefix: "$",
        variable: true,
      },
    ],
  },
  {
    title: "Variable Costs",
    icon: Scale,
    description:
      "Enter costs that vary with sales volume, such as shipping, transaction fees, and inventory costs",
    inputs: [
      {
        name: "cogs",
        label: "Fallback COGS",
        tooltip:
          "We use this to calculate COGS when inventory cost was not provided",
        prefix: "%",
        variable: true,
      },
      {
        name: "noExternalCogs",
        label: "Only use Fallback COGS",
        tooltip:
          "If checked, we will only use the Fallback COGS and ignore any COGS provided by your store",
        type: "switch",
      },
      {
        name: "shippingCost",
        label: "Carrier Fees per Order",
        tooltip: "Average carrier fees for each order",
        prefix: "$",
      },
      {
        name: "transactionFee",
        label: "Payment Processing Fees per Order",
        tooltip: "Percentage of each order total charged by payment processors",
        prefix: "%",
      },
    ],
  },
  {
    title: "Marketing Costs",
    icon: Megaphone,
    description:
      "Enter costs related to other marketing activities, such as influencers, SMS, events, etc",
    inputs: [
      {
        name: "marketingInfluencers",
        label: "Influencers",
        prefix: "$",
        variable: true,
      },
      {
        name: "marketingEmail",
        label: "Email & SMS",
        prefix: "$",
        variable: true,
      },
      {
        name: "marketingOther",
        label: "Other Direct Marketing",
        prefix: "$",
        variable: true,
      },
    ],
  },
];

const defaultInputs = [
  {
    period: "default",
    ...sections.reduce(
      (acc, section) => ({
        ...acc,
        ...section.inputs.reduce((inputAcc, input) => {
          inputAcc[input.name] = 0; // Default value
          return inputAcc;
        }, {}),
      }),
      {}
    ),
  },
];

function PrefixedInput({ prefix, monthly = false, children, ...props }) {
  const prefixRef = useRef(null);
  const [prefixWidth, setPrefixWidth] = useState(0);

  useLayoutEffect(() => {
    const element = prefixRef.current;
    if (!element) return;

    const measure = () => setPrefixWidth(element.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [prefix]);

  return (
    <div
      className={cn("relative w-full", monthly && "p-1")}
      style={{ "--input-prefix-width": `${prefixWidth}px` }}
    >
      {prefix && (
        <span
          ref={prefixRef}
          className={cn(
            "text-muted-foreground pointer-events-none absolute top-1/2 -translate-y-1/2 font-sans text-base md:text-sm",
            monthly ? "left-1.5 sm:left-2.5" : "left-2.5"
          )}
        >
          {prefix}
        </span>
      )}
      {/* Keep the input's background, border and focus ring around the prefix. */}
      <Input
        {...props}
        className={cn(
          monthly && "border-none dark:bg-transparent",
          monthly && prefix?.length > 1 && "pr-1",
          prefix &&
            (monthly
              ? "pl-[max(0.875rem,calc(var(--input-prefix-width)+0.25rem))] sm:pl-[max(1.25rem,calc(var(--input-prefix-width)+0.5rem))]"
              : "pl-[max(1.5rem,calc(var(--input-prefix-width)+0.875rem))]")
        )}
      />
      {children}
    </div>
  );
}

function MonthlyInput({
  months,
  prefix,
  onChange,
  onBlur,
  disabled, // either boolean or the month getting updated
  displayValues = {},
}) {
  return (
    <ScrollArea>
      <div
        className={cn(
          "border-input grid min-w-xl grid-cols-6 overflow-hidden rounded-md border",
          prefix?.length > 1 && "min-w-[48rem]"
        )}
      >
        {months.map((month) => (
          <div key={month} className="flex flex-col items-center">
            <div className="bg-muted w-full py-1">
              <Label
                htmlFor={month}
                className="text-muted-foreground flex w-full justify-center text-xs"
              >
                {new Date(`${month} 1`).toLocaleString("default", {
                  month: "short",
                })}
              </Label>
            </div>
            <PrefixedInput
              monthly
              prefix={prefix}
              id={month}
              value={displayValues[month] || ""}
              onChange={onChange(month)}
              onBlur={onBlur(month)}
              disabled={disabled === true || disabled === month}
            >
              <div className="absolute bottom-2 left-0 mx-4 h-px w-[calc(100%-var(--spacing)*8)] border-b border-dashed"></div>
            </PrefixedInput>
          </div>
        ))}
      </div>

      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}

function InputField({
  name,
  label,
  prefix,
  type = "text", // or "switch"
  tooltip,
  inputs,
  updateInput,
  variable,
  useMonthly,
  disabled, // either boolean or the month getting updated
  addYearToQuery,
  formatter,
}) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [draftValues, setDraftValues] = useState({});
  const displayValues = {
    ...inputs?.reduce((acc, input) => {
      const value = input[name];
      acc[input.period] =
        value && formatter(prefix === "%" ? value * 100 : value);
      return acc;
    }, {}),
    ...draftValues,
  };

  const onBlur = (period) => async (e) => {
    const text = e.target.value;
    const value = parseFloat(text?.trim()?.replace(/[^0-9e.-]+/g, "") || "0");

    if (isNaN(value)) {
      return;
    }
    const actualValue = prefix === "%" ? value / 100 : value;

    // check if changed
    const prev = inputs.find((input) => input.period === period)?.[name];
    if (prev !== actualValue && !(prev === undefined && actualValue === 0)) {
      await updateInput({
        period,
        [name]: actualValue,
      });
    }

    // Reveal the latest saved value, without discarding a newer edit.
    setDraftValues((drafts) => {
      if (drafts[period] !== text) return drafts;
      const next = { ...drafts };
      delete next[period];
      return next;
    });
  };

  const onChange = (period) => (e) => {
    setDraftValues((prev) => ({
      ...prev,
      [period]: e.target.value,
    }));
  };

  return (
    <div className="grid w-full items-center gap-2">
      <div
        className={cn(
          "flex flex-row gap-x-4 sm:items-center",
          type === "switch" &&
            "bg-input/30 border-input h-9 rounded-md border px-2"
        )}
      >
        {type === "switch" && (
          <div className="flex items-center">
            <Switch
              id={name}
              onCheckedChange={(checked) => {
                updateInput({
                  period: "default",
                  [name]: checked,
                });
              }}
              checked={
                !!inputs?.find((input) => input.period === "default")?.[name]
              }
              disabled={disabled}
            />
          </div>
        )}

        <div className="flex h-6 items-center gap-2">
          <Label htmlFor={name}>{label}</Label>
          {tooltip && (
            <Tooltip delayDuration={400}>
              <TooltipTrigger>
                <Info className="text-muted-foreground size-4" />
              </TooltipTrigger>

              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          )}
        </div>

        <div className="flex flex-1 flex-col-reverse items-end justify-center gap-2 sm:flex-row sm:items-center">
          {useMonthly && (
            <Select
              value={year}
              onValueChange={(newYear) => {
                setYear(newYear);
                addYearToQuery(newYear);
              }}
              disabled={disabled}
            >
              <SelectTrigger size="xs" className="border-none">
                <SelectValue placeholder="Select the year" />
              </SelectTrigger>
              <SelectContent className="min-w-10">
                <SelectItem value={new Date().getFullYear() + 1}>
                  {new Date().getFullYear() + 1}
                </SelectItem>
                <SelectItem value={new Date().getFullYear()}>
                  {new Date().getFullYear()}
                </SelectItem>
                <SelectItem value={new Date().getFullYear() - 1}>
                  {new Date().getFullYear() - 1}
                </SelectItem>
                <SelectItem value={new Date().getFullYear() - 2}>
                  {new Date().getFullYear() - 2}
                </SelectItem>
              </SelectContent>
            </Select>
          )}

          {variable && (
            <div className="ml-auto flex h-6 items-center space-x-2">
              <Switch
                id="variable"
                disabled={disabled}
                onCheckedChange={() => {
                  updateInput(undefined, !useMonthly);
                }}
                checked={useMonthly}
              />
              <Label htmlFor="variable" className="text-muted-foreground">
                Custom Monthly Input
              </Label>
            </div>
          )}
        </div>
      </div>

      {useMonthly ? (
        <MonthlyInput
          months={getMonthsInAYear(year)}
          prefix={prefix}
          inputs={inputs}
          disabled={disabled}
          onBlur={onBlur}
          onChange={onChange}
          displayValues={displayValues}
          formatter={formatter}
          fieldName={name}
        />
      ) : (
        type === "text" && (
          <PrefixedInput
            prefix={prefix}
            id={name}
            autoComplete="nope"
            value={displayValues["default"] || ""}
            onChange={onChange("default")}
            onBlur={onBlur("default")}
            disabled={disabled}
          />
        )
      )}
    </div>
  );
}

function InputsAccordionItem({ title, icon: Icon, description, children }) {
  return (
    <AccordionItem value={title}>
      <AccordionTrigger>
        <div className="flex items-center gap-2">
          {Icon && <Icon className="text-muted-foreground size-4" />}
          {title}
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <p className="text-muted-foreground -mt-3 mb-2 text-sm">
          {description}
        </p>
        <div className="mt-4 flex flex-col gap-4">{children}</div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function InputsSection(props) {
  return (
    <StoreInputsSection
      key={`${props.store.id}:${props.store.currency}`}
      {...props}
    />
  );
}

function StoreInputsSection({
  store,
  updateIntegration,
  refetchStore,
  sectionId,
  className,
  readOnly = false,
}) {
  const { currentUser } = useAuth();
  const numberFormat = useNumberFormat(store.currency);
  const [inputs, setInputs] = useState(defaultInputs);
  const [useMonthly, setUseMonthly] = useState({});
  const reviewedAt = store?.settings?.onboarding?.manualInputsReviewedAt;
  const [hasAutoReviewed, setHasAutoReviewed] = useState(false);

  const { queryApi: getAnalyticsInputs, loading: queryLoading } = useApiQuery({
    query: ANALYTICS_INPUTS_QUERY,
  });
  const { mutationApi: upsertAnalyticsInputs, loading: mutationLoading } =
    useApiMutation({
      mutation: UPSERT_ANALYTICS_INPUTS_MUTATION,
    });
  const loading = queryLoading;

  const [mutatingInputs, setMutatingInputs] = useState({});

  useEffect(() => {
    setHasAutoReviewed(false);
  }, [store.id]);

  const updateInput = useMemo(
    () => (inputName) => {
      return (newInput, newUseMonthly) => {
        setMutatingInputs((prev) => ({
          ...prev,
          [inputName]: newInput?.period || "default",
        }));

        return (
          upsertAnalyticsInputs({
            inputs: newInput ? [newInput] : undefined,
            useMonthly:
              newUseMonthly !== undefined
                ? {
                    [inputName]: newUseMonthly,
                  }
                : undefined,
            integrationId: store.id,
          })
            .then(({ data }) => {
              if (!data?.upsertAnalyticsInputs) return;
              if (newInput) {
                setInputs((prev) => {
                  const exists = prev.some(
                    (input) => input.period === newInput.period
                  );
                  return exists
                    ? prev.map((input) =>
                        input.period === newInput.period
                          ? { ...input, ...newInput }
                          : input
                      )
                    : [...prev, newInput];
                });
              }
              if (newUseMonthly !== undefined) {
                setUseMonthly((prev) => ({
                  ...prev,
                  [inputName]: newUseMonthly,
                }));
              }
            })
            // useApiMutation reports failures; retain the last confirmed settings.
            .catch(() => {})
            .finally(() =>
              setMutatingInputs((prev) => {
                const updated = { ...prev };
                delete updated[inputName];
                return updated;
              })
            )
        );
      };
    },
    [store.id, upsertAnalyticsInputs]
  );

  const [periodsToFetch, setPeriodsToFetch] = useState([
    "default",
    ...getMonthsInAYear(new Date().getFullYear()),
  ]);
  useEffect(() => {
    let active = true;
    const fetchAnalyticsInputs = async () => {
      const { data } = await getAnalyticsInputs({
        integrationId: store.id,
        periods: periodsToFetch,
      });

      if (!active || !data) return;
      setInputs(
        data.analyticsInputs?.inputs?.length
          ? data.analyticsInputs.inputs
          : defaultInputs
      );
      setUseMonthly(data.analyticsInputs?.useMonthly || {});
    };

    fetchAnalyticsInputs();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.id, periodsToFetch]);

  const markManualInputsReviewed = async () => {
    if (reviewedAt || hasAutoReviewed) {
      return;
    }

    setHasAutoReviewed(true);

    try {
      await updateIntegration({
        id: store.id,
        input: {
          settings: {
            ...(store.settings || {}),
            onboarding: {
              ...(store.settings?.onboarding || {}),
              manualInputsReviewedAt: new Date().toISOString(),
              manualInputsReviewedByUserId: currentUser?.id || null,
            },
          },
        },
      });
      await refetchStore?.();
    } catch (error) {
      setHasAutoReviewed(false);
      throw error;
    }
  };

  return (
    <SettingsSection
      title="Manual Inputs"
      description={`Configure manual inputs for your store's analytics. Monetary amounts are in ${numberFormat.currencyCode} (${numberFormat.currencySymbol}).`}
      sectionId={sectionId}
      className={className}
    >
      <Accordion
        type="multiple"
        onValueChange={(value) => {
          if (!value.length || loading || mutationLoading || readOnly) {
            return;
          }

          void markManualInputsReviewed();
        }}
      >
        {sections.map((section) => (
          <InputsAccordionItem
            key={section.title}
            title={section.title}
            description={section.description}
            icon={section.icon}
          >
            {section.inputs.map((input) => (
              <InputField
                key={input.name}
                {...input}
                prefix={
                  input.prefix === "$"
                    ? numberFormat.currencySymbol
                    : input.prefix
                }
                inputs={inputs}
                updateInput={updateInput(input.name)}
                useMonthly={useMonthly[input.name] || false}
                disabled={readOnly || loading || !!mutatingInputs?.[input.name]}
                addYearToQuery={(newYear) => {
                  const newPeriods = [
                    ...new Set([
                      ...periodsToFetch,
                      ...getMonthsInAYear(newYear),
                    ]),
                  ];

                  if (periodsToFetch.toString() !== newPeriods.toString()) {
                    setPeriodsToFetch(newPeriods);
                  }
                }}
                formatter={numberFormat.whole}
              />
            ))}
          </InputsAccordionItem>
        ))}
      </Accordion>
    </SettingsSection>
  );
}
